import React from 'react';
import { ArrowUpRight, BarChart3, Zap, Database, Workflow, Bot, Cpu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { integrationTags } from '../mock';
import ConsultationCalendar from './ConsultationCalendar';

const iconFor = (tag) => {
  const map = {
    'API Orchestration': Zap, 'Data system': Database, 'Workflow': Workflow,
    'CRM integration': Cpu, 'Intelligent routing': Workflow, 'Lead scoring': BarChart3,
    'Chatbot': Bot, 'RPA solution': Zap, 'AI assistants': Bot, 'Model deployment': Cpu,
    'Prediction': BarChart3, 'Data synchronization': Database, 'Process opt': Workflow
  };
  return map[tag] || Zap;
};

const Chip = ({ tag }) => {
  const Icon = iconFor(tag);
  return (
    <span className="chip">
      <Icon size={14} className="text-amber-400" />
      {tag}
    </span>
  );
};

const MarqueeRow = ({ list, reverse = false, speed = 'animate-marquee' }) => (
  <div className="overflow-hidden py-1">
    <div className={`flex gap-3 w-max ${speed}`} style={reverse ? { animationDirection: 'reverse' } : {}}>
      {[...list, ...list].map((t, i) => <Chip key={`${t}-${i}`} tag={t} />)}
    </div>
  </div>
);

const Calendar = () => <ConsultationCalendar />;

const BarChart = () => {
  const bars = [
    { id: 'jan', h: 45, highlight: false },
    { id: 'feb', h: 80, highlight: true },
    { id: 'mar', h: 55, highlight: false },
    { id: 'apr', h: 95, highlight: true },
    { id: 'may', h: 60, highlight: false },
    { id: 'jun', h: 40, highlight: false },
    { id: 'jul', h: 30, highlight: false },
    { id: 'aug', h: 25, highlight: false },
  ];
  return (
    <div className="rounded-2xl p-5 bg-[#0b1220]/60 border border-white/10">
      <div className="flex items-center gap-2 mb-4 text-slate-300 text-sm">
        <BarChart3 size={14} className="text-amber-400" />
        <span className="font-medium text-white">10</span>
        <span className="text-slate-400">Major Optimizations</span>
      </div>
      <div className="h-40 flex items-end gap-2">
        {bars.map((bar) => (
          <div
            key={bar.id}
            className={`flex-1 rounded-md ${bar.highlight ? 'bg-amber-400 shadow-[0_0_20px_rgba(212,175,55,0.5)]' : 'bg-white/10'}`}
            style={{ height: `${bar.h}%` }}
          ></div>
        ))}
      </div>
    </div>
  );
};

const Expertise = () => {
  const row1 = integrationTags.slice(0, 7);
  const row2 = integrationTags.slice(3, 10);
  const row3 = integrationTags.slice(5, 13);

  return (
    <section className="relative py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="section-label mb-6">Our Expertise</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
            Solutions that drive <br className="hidden md:block" /> intelligence
          </h2>
          <p className="text-slate-400 max-w-md">
            End-to-end AI services built to automate & accelerate growth.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Card 1: AI Consulting */}
          <div className="card-dark rounded-3xl p-7 flex flex-col justify-between min-h-[440px]">
            <div>
              <div className="flex items-start justify-between">
                <h3 className="text-white text-xl font-semibold mb-2">AI Consulting</h3>
                <Link to="/ai-consulting" target="_blank" rel="noreferrer" className="h-8 w-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center hover:bg-amber-500/25 transition-colors" aria-label="Explore AI Consulting Services">
                  <ArrowUpRight size={14} className="text-amber-300" />
                </Link>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Define AI strategies, identify opportunities, and align implementation with business goals.
              </p>
              <Link to="/ai-consulting" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-amber-400 text-sm mt-4 hover:text-amber-300">
                Explore full services <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="mt-6"><Calendar /></div>
          </div>

          {/* Card 2: Intelligent Workflow */}
          <div className="card-dark rounded-3xl p-7 flex flex-col justify-between min-h-[440px]">
            <div>
              <h3 className="text-white text-xl font-semibold mb-2">Intelligent Workflow Optimization</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Streamline operations through automation and data-driven systems designed to improve speed, accuracy, and efficiency.
              </p>
            </div>
            <div className="mt-6"><BarChart /></div>
          </div>

          {/* Card 3: AI Integration & Automation */}
          <div className="card-dark rounded-3xl p-7 flex flex-col justify-between min-h-[440px] overflow-hidden">
            <div>
              <h3 className="text-white text-xl font-semibold mb-2">AI Integration & Automation</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Seamlessly integrate AI tools into existing platforms and operational systems.
              </p>
            </div>
            <div className="mt-6 -mx-7 space-y-2">
              <MarqueeRow list={row1} />
              <MarqueeRow list={row2} reverse speed="animate-marquee-slow" />
              <MarqueeRow list={row3} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Expertise;
